/**
 * `Scenes_Registry.ts` — port of `AI/Registry/Scenes_Registry.cs`.
 *
 * Web slice: action playback tools (`play_action`, `pause_current_action`,
 * `continue_current_action`, `toggle_action_button_visibility`) are deferred
 * until an actions model exists, so they are not registered.
 */

import { AgentToolNames } from "./agentToolNames";
import {
  type AIAgentTool,
  buildParams,
  numberParam,
  stringParam,
} from "./AIAgentToolRegistryModel";

export function addScenesTools(tools: AIAgentTool[]): void {
  tools.push(
    {
      name: AgentToolNames.NewScene,
      category: "Authoring",
      description:
        "Creates a new empty scene at the end of the project's scenes list and navigates to it.",
      humanDescription: "Create a new empty scene and switch to it.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.DuplicateScene,
      category: "Authoring",
      description:
        "Duplicates the currently active scene (entities, selection sets, scene settings) and navigates to the new scene. Campfire automatically smooths transitions between scenes when the same parts are moved differently across them.",
      humanDescription:
        "Duplicate the current scene, including entities, selection sets, and scene settings.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.GoToScene,
      category: "Navigation",
      description: `Navigates to a scene by its displayed number (1-based). Use ${AgentToolNames.GetProjectContext} to see available scenes.`,
      humanDescription: "Open a specific scene by number.",
      parameters: buildParams({
        sceneNumber: numberParam("1-based visible scene number.", false),
      }),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GoToNextScene,
      category: "Navigation",
      description:
        "Navigates to the next scene. Use when the user explicitly asks for the next scene, OR for brief move-on confirmations (continue, I'm ready, let's continue, ready, proceed, keep going, go ahead, yes, okay, let's go). For those brief move-on requests, check Current Project Context before responding: if any Available Scene appears after the current scene, use this tool and do not answer conversationally. Only say there is no next scene when the current scene is the final Available Scene in Current Project Context. No-op if already on the last scene.",
      humanDescription: "Move to the next scene.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.GoToPreviousScene,
      category: "Navigation",
      description:
        "Navigates to the previous scene. Use ONLY when the user explicitly asks for the previous scene; do not infer from generic continue/proceed/ready replies. No-op if already on the first scene.",
      humanDescription: "Move to the previous scene.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.ResetSceneView,
      category: "Navigation",
      labelWhenActive: "Resetting scene view...",
      description: `Resets the current scene camera to the saved/default scene view (same as Reset camera in the editor UI), with a smooth transition. Camera-only - do NOT use ${AgentToolNames.ResetScene} for view-only requests.`,
      humanDescription: "Reset the current scene camera to the saved/default scene view.",
      parameters: buildParams(),
      enabled: true,
      writerOnly: false,
    },
    {
      name: AgentToolNames.RenameScene,
      category: "Authoring",
      description: `Renames a scene. Defaults to the currently active scene when sceneNumber is omitted. Use ${AgentToolNames.GetProjectContext} to see scene numbers (1-based).`,
      humanDescription: "Rename the current scene or another scene by number.",
      parameters: buildParams({
        sceneNumber: numberParam("1-based visible scene number. Omit to rename the active scene."),
        newName: stringParam("New scene name.", false),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.UpdateSceneNotes,
      category: "Authoring",
      description: `Replaces the presenter notes (description) for a scene. Defaults to the currently active scene when sceneNumber is omitted. Use ${AgentToolNames.GetProjectContext} to see scene numbers (1-based).`,
      humanDescription:
        "Edit the presenter notes or description for the current scene or another scene.",
      parameters: buildParams({
        sceneNumber: numberParam("1-based visible scene number. Omit to update the active scene."),
        newNotes: stringParam(
          "Complete notes/description that should replace the existing notes.",
          false,
        ),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.ReorderScene,
      category: "Authoring",
      description: `Moves a scene to a new position in the scenes list. Defaults to the active scene when sceneNumber is omitted. Both numbers are 1-based. Use ${AgentToolNames.GetProjectContext} to see scene numbers.`,
      humanDescription: "Move a scene to a new position in the project scene list.",
      parameters: buildParams({
        sceneNumber: numberParam("1-based scene number to reorder. Omit to use the active scene."),
        newSceneNumber: numberParam("1-based new position in the scenes list.", false),
      }),
      enabled: true,
      writerOnly: true,
    },
    {
      name: AgentToolNames.DeleteScene,
      category: "Authoring",
      description: `Deletes a scene. Will NOT delete the active scene unless sceneNumber explicitly specifies it; if it does, the tool auto-navigates to another scene afterward (or no scene if it was the last). Use ${AgentToolNames.GetProjectContext} to see scene numbers (1-based). WARNING: cannot be undone.`,
      humanDescription: "Delete a scene by number.",
      parameters: buildParams({
        sceneNumber: numberParam("1-based scene number to delete.", false),
      }),
      enabled: true,
      writerOnly: true,
    },
  );
}
